import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  InputAdornment
} from '@mui/material';
import { PiPlus, PiTrash, PiMapPin } from 'react-icons/pi';

interface UIElement {
  type: string;
  config: {
    options?: string[];
    label?: string;
    required?: boolean;
    // used by address_list (and other free-text list inputs)
    placeholder?: string;
    help_text?: string;
    field_name?: string;
    min_items?: number;
    max_items?: number;
    default?: any;
  };
}

// GUI list input for addresses: type an address, press Enter or click Add, and it appears
// as a removable row. Much clearer than asking users to comma/newline separate values.
// Pasting a multi-line or comma-separated blob still works — each entry is added as its own row.
function AddressListInput({
  element,
  value,
  onChange,
  disabled
}: {
  element: UIElement;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [pending, setPending] = React.useState('');
  const isRequired = element.config.required === true;
  const maxItems = element.config.max_items;
  const atMax = typeof maxItems === 'number' && value.length >= maxItems;
  const isMissing = isRequired && value.length === 0;

  const addPending = () => {
    // split only on newlines so a pasted multi-line list becomes multiple rows, but a single
    // typed address keeps its internal commas (e.g. "1000 S. Fremont Avenue, Alhambra" = one address).
    const parts = pending
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    // de-dupe against existing + within the pasted batch, respect max_items
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const additions: string[] = [];
    for (const p of parts) {
      const key = p.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      additions.push(p);
    }
    let next = [...value, ...additions];
    if (typeof maxItems === 'number') next = next.slice(0, maxItems);
    onChange(next);
    setPending('');
  };

  const removeAt = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <FormControl fullWidth disabled={disabled} error={isMissing}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {element.config.label || 'Addresses'}
        {isRequired ? ' *' : ''}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={element.config.placeholder || 'Enter a street address'}
          value={pending}
          disabled={disabled || atMax}
          onChange={(e) => setPending(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addPending();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PiMapPin style={{ opacity: 0.6 }} />
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="outlined"
          onClick={addPending}
          disabled={disabled || atMax || pending.trim().length === 0}
          startIcon={<PiPlus />}
          sx={{ flexShrink: 0, height: 40 }}
        >
          Add
        </Button>
      </Box>

      {value.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 1 }}>
          <List dense disablePadding>
            {value.map((addr, idx) => (
              <ListItem
                key={`${addr}-${idx}`}
                divider={idx < value.length - 1}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove ${addr}`}
                    onClick={() => removeAt(idx)}
                    disabled={disabled}
                    size="small"
                  >
                    <PiTrash />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${idx + 1}. ${addr}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <FormHelperText>
        {isMissing
          ? 'Add at least one address to run this workflow'
          : atMax
          ? `Maximum of ${maxItems} addresses reached`
          : (element.config.help_text ||
            `${value.length} address${value.length === 1 ? '' : 'es'} added. Type an address and press Enter or click Add.`)}
      </FormHelperText>
    </FormControl>
  );
}

interface WorkflowUIElementsProps {
  elements: UIElement[];
  values: Record<string, any>;
  onChange: (elementType: string, value: any) => void;
  disabled?: boolean;
}

export default function WorkflowUIElements({ 
  elements, 
  values, 
  onChange, 
  disabled = false 
}: WorkflowUIElementsProps) {
  if (!elements || elements.length === 0) {
    return null;
  }

  const renderElement = (element: UIElement, index: number) => {
    switch (element.type) {
      case 'address_list': {
        // GUI list input: add addresses one at a time (or paste a batch). stored under
        // config.field_name (defaults to 'addresses') so the recipe's "$.addresses" resolves.
        const fieldName = element.config.field_name || 'addresses';
        const currentValue: string[] = Array.isArray(values[fieldName]) ? values[fieldName] : [];
        return (
          <AddressListInput
            key={`${element.type}-${index}`}
            element={element}
            value={currentValue}
            onChange={(next) => onChange(fieldName, next)}
            disabled={disabled}
          />
        );
      }

      case 'text_input': {
        // generic single-value text field. used by cortese-list for the optional area search
        // (zip / city / county), but intentionally not Cortese-specific.
        const fieldName = element.config.field_name || element.type;
        const isRequired = element.config.required === true;
        const currentValue = values[fieldName] == null ? '' : String(values[fieldName]);
        const isMissing = isRequired && currentValue.trim().length === 0;
        return (
          <FormControl fullWidth key={`${element.type}-${index}`} disabled={disabled} error={isMissing}>
            <TextField
              label={`${element.config.label || 'Value'}${isRequired ? ' *' : ''}`}
              placeholder={element.config.placeholder || ''}
              size="small"
              value={currentValue}
              disabled={disabled}
              error={isMissing}
              onChange={(e) => onChange(fieldName, e.target.value)}
            />
            <FormHelperText>
              {isMissing
                ? `${element.config.label || 'This field'} is required`
                : element.config.help_text || ''}
            </FormHelperText>
          </FormControl>
        );
      }

      case 'party_role': {
        const isRequired = element.config.required === true;
        const isMissing = isRequired && !values[element.type];
        return (
          <FormControl fullWidth key={`${element.type}-${index}`} disabled={disabled} required={isRequired} error={isMissing}>
            <InputLabel>Party Role{isRequired ? ' *' : ''}</InputLabel>
            <Select
              value={values[element.type] || ''}
              onChange={(e) => onChange(element.type, e.target.value)}
              label={`Party Role${isRequired ? ' *' : ''}`}
            >
              {element.config.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {isMissing ? 'Party Role is required to execute this workflow' : 'Select your role in this matter (affects prompt context)'}
            </FormHelperText>
          </FormControl>
        );
      }

      case 'podcast_quality':
        return (
          <FormControl fullWidth key={`${element.type}-${index}`} disabled={disabled}>
            <InputLabel>{element.config.label || 'Audio Quality'}</InputLabel>
            <Select
              value={values[element.type] || element.config.default || 'standard'}
              onChange={(e) => onChange(element.type, e.target.value)}
              label={element.config.label || 'Audio Quality'}
            >
              <MenuItem value="standard">
                <Box>
                  <Typography variant="body1">Standard</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~2 min generation time
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="high">
                <Box>
                  <Typography variant="body1">High</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~9 min — more natural-sounding voices
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="premium">
                <Box>
                  <Typography variant="body1">Premium</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~28 min — most expressive, LLM-driven prosody
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText>
              {element.config.help_text || 'Higher quality uses more natural voices but takes longer'}
            </FormHelperText>
          </FormControl>
        );

      case 'podcast_duration':
        return (
          <FormControl fullWidth key={`${element.type}-${index}`} disabled={disabled}>
            <InputLabel>{element.config.label || 'Target Duration'}</InputLabel>
            <Select
              value={values[element.type] == null ? '' : values[element.type]}
              onChange={(e) => onChange(element.type, e.target.value === '' ? null : Number(e.target.value))}
              label={element.config.label || 'Target Duration'}
              displayEmpty
            >
              <MenuItem value="">
                <Box>
                  <Typography variant="body1">Auto</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Let the service decide
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={30}>
                <Box>
                  <Typography variant="body1">Short</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~30 min
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={60}>
                <Box>
                  <Typography variant="body1">Medium</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~60 min
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={180}>
                <Box>
                  <Typography variant="body1">Long</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~3h
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText>
              {element.config.help_text || 'Approximate target length. Longer episodes take more time to generate.'}
            </FormHelperText>
          </FormControl>
        );

      default:
        return (
          <Box key={`unknown-${index}`} sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Unknown UI element type: {element.type}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Workflow Configuration
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {elements.map((element, index) => renderElement(element, index))}
      </Box>
    </Box>
  );
}